import React, { useState, useEffect } from "react";
import { RibaStage, Project, InformationExchange } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { FileUpload } from "./FileUpload";
import { useProject } from "@/contexts/ProjectContext";
import { CheckCircle, FileCheck, AlertTriangle, Clock, Upload, Info, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";

interface StageInformationExchangeProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

// Define required information exchanges for each RIBA stage
const requiredExchangesByStage: Record<number, { name: string; description: string; requiredForSignoff: boolean }[]> = {
  0: [
    { name: "Client Requirements", description: "Document outlining the client's initial requirements", requiredForSignoff: true },
    { name: "Business Case", description: "Business justification for the project", requiredForSignoff: true },
  ],
  1: [
    { name: "Project Brief", description: "Detailed project brief including objectives, sustainability goals, and spatial needs", requiredForSignoff: true },
    { name: "Site Information", description: "Key details and surveys about the project site", requiredForSignoff: true },
    { name: "Project Programme", description: "Key milestones and programmed dates", requiredForSignoff: true },
    { name: "Risk Register", description: "Initial risk assessment and mitigation strategy", requiredForSignoff: true },
    { name: "Project Execution Plan", description: "Plan detailing project management approach", requiredForSignoff: true },
  ],
  2: [
    { name: "Concept Design", description: "Complete concept design documentation", requiredForSignoff: true },
    { name: "Outline Specification", description: "Initial specification of materials and systems", requiredForSignoff: true },
    { name: "Project Strategies", description: "Documentation of key project strategies", requiredForSignoff: false },
    { name: "Initial Cost Plan", description: "Initial cost estimates and budget breakdown", requiredForSignoff: true },
  ],
  3: [
    { name: "Spatially Coordinated Design", description: "Detailed spatial design with coordination between disciplines", requiredForSignoff: true },
    { name: "Updated Specification", description: "Detailed specifications for all elements", requiredForSignoff: true },
    { name: "Updated Cost Plan", description: "Refined cost estimates based on detailed design", requiredForSignoff: true },
    { name: "Planning Application Submission", description: "Documents submitted for planning approval (if required)", requiredForSignoff: false },
  ],
  4: [
    { name: "Technical Design", description: "Complete technical design suitable for construction", requiredForSignoff: true },
    { name: "Final Specifications", description: "Final detailed specifications for all elements", requiredForSignoff: true },
    { name: "Residual Project Strategies", description: "Final strategies for project delivery", requiredForSignoff: true },
  ],
  5: [
    { name: "Construction Information", description: "Detailed information for construction teams", requiredForSignoff: true },
    { name: "Building Manual", description: "Preliminary building manual and operating instructions", requiredForSignoff: true },
    { name: "Compliance Certifications", description: "Documentation confirming compliance with regulations", requiredForSignoff: true },
  ],
  6: [
    { name: "Final Completion File", description: "Complete project documentation at completion", requiredForSignoff: true },
    { name: "Defects List", description: "List of identified defects requiring resolution", requiredForSignoff: true },
    { name: "Asset Information", description: "Detailed information about all building assets", requiredForSignoff: true },
  ],
  7: [
    { name: "Facilities Management Information", description: "Information required for ongoing facility management", requiredForSignoff: true },
    { name: "Post-Occupancy Evaluation", description: "Assessment of building performance post-completion", requiredForSignoff: true },
    { name: "Sustainability Outcomes Verification", description: "Verification of achieved sustainability targets", requiredForSignoff: true },
  ],
};

export default function StageInformationExchange({ project, stage, onUpdate }: StageInformationExchangeProps) {
  const { updateStage } = useProject();
  const [infoExchanges, setInfoExchanges] = useState<InformationExchange[]>(stage.informationExchanges || []);
  const [currentExchange, setCurrentExchange] = useState<InformationExchange | null>(null);
  const [comment, setComment] = useState("");
  const [commentDialogOpen, setCommentDialogOpen] = useState(false);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [readinessDialogOpen, setReadinessDialogOpen] = useState(false);
  const [missingRequiredItems, setMissingRequiredItems] = useState<string[]>([]);
  
  // Initialize exchanges if they don't exist
  useEffect(() => {
    if (!stage.informationExchanges || stage.informationExchanges.length === 0) {
      const requiredExchanges = requiredExchangesByStage[stage.id] || [];
      const initialExchanges: InformationExchange[] = requiredExchanges.map((item, index) => ({
        id: `exchange-${stage.id}-${index}`,
        name: item.name,
        description: item.description,
        status: 'pending',
        requiredForSignoff: item.requiredForSignoff,
      }));
      
      setInfoExchanges(initialExchanges);
      
      // If there are exchanges to initialize, update the stage
      if (initialExchanges.length > 0) {
        const updatedStage = {
          ...stage,
          informationExchanges: initialExchanges,
        };
        updateStage(project.id, stage.id, updatedStage);
        
        if (onUpdate) {
          onUpdate();
        }
      }
    } else {
      setInfoExchanges(stage.informationExchanges);
    }
  }, [stage.id, stage.informationExchanges]);

  const handleUpload = (exchangeId: string, url: string) => {
    const updatedExchanges: InformationExchange[] = infoExchanges.map(exchange => 
      exchange.id === exchangeId
        ? { ...exchange, documentUrl: url, status: 'complete', dateUploaded: new Date() }
        : exchange
    );
    
    setInfoExchanges(updatedExchanges);
    
    const updatedStage = {
      ...stage,
      informationExchanges: updatedExchanges,
    };
    
    updateStage(project.id, stage.id, updatedStage);
    
    toast({
      title: "Document uploaded",
      description: "Information exchange document has been uploaded successfully.",
    });
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const openCommentDialog = (exchange: InformationExchange) => {
    setCurrentExchange(exchange);
    setComment(exchange.comments || "");
    setCommentDialogOpen(true);
  };
  
  const handleSaveComment = () => {
    if (!currentExchange) return;
    
    const updatedExchanges: InformationExchange[] = infoExchanges.map(exchange => 
      exchange.id === currentExchange.id
        ? { ...exchange, comments: comment }
        : exchange
    );
    
    setInfoExchanges(updatedExchanges);
    
    const updatedStage = {
      ...stage,
      informationExchanges: updatedExchanges,
    };
    
    updateStage(project.id, stage.id, updatedStage);
    
    toast({
      title: "Comment saved",
      description: "Your comment has been saved successfully.",
    });
    
    setCommentDialogOpen(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  const checkStageReadiness = () => {
    // Check if all required exchanges are complete
    const missing = infoExchanges
      .filter(exchange => exchange.requiredForSignoff && exchange.status !== 'complete' && exchange.status !== 'validated')
      .map(exchange => exchange.name);
    
    if (missing.length > 0) {
      setMissingRequiredItems(missing);
      setReadinessDialogOpen(true);
      return false;
    }
    
    return true;
  };
  
  const validateStage = () => {
    // Check if all required exchanges are complete
    const missing = infoExchanges
      .filter(exchange => exchange.requiredForSignoff && exchange.status !== 'complete' && exchange.status !== 'validated')
      .map(exchange => exchange.name);
    
    if (missing.length > 0) {
      setMissingRequiredItems(missing);
      setValidationDialogOpen(true);
      return false;
    }
    
    // Mark all complete items as validated
    const validatedExchanges: InformationExchange[] = infoExchanges.map(exchange => 
      exchange.status === 'complete'
        ? { ...exchange, status: 'validated' }
        : exchange
    );
    
    setInfoExchanges(validatedExchanges);
    
    const updatedStage = {
      ...stage,
      informationExchanges: validatedExchanges,
    };
    
    updateStage(project.id, stage.id, updatedStage);
    
    toast({
      title: "Stage validated",
      description: "All required information exchanges for this stage are complete.",
    });
    
    if (onUpdate) {
      onUpdate();
    }
    
    return true;
  };
  
  const getStatusIcon = (status: 'pending' | 'complete' | 'validated') => {
    if (status === 'validated') return <CheckCircle className="h-5 w-5 text-green-600" />;
    if (status === 'complete') return <FileCheck className="h-5 w-5 text-blue-600" />;
    return <Clock className="h-5 w-5 text-amber-600" />;
  };
  
  const getStatusText = (status: 'pending' | 'complete' | 'validated') => {
    if (status === 'validated') return "Validated";
    if (status === 'complete') return "Complete";
    return "Pending";
  };

  // Calculate completion percentage
  const completionPercentage = infoExchanges.length > 0
    ? Math.round((infoExchanges.filter(ex => ex.status !== 'pending').length / infoExchanges.length) * 100)
    : 0;
  
  // Calculate readiness score
  const calculateReadinessScore = () => {
    if (infoExchanges.length === 0) return 0;
    
    const requiredItems = infoExchanges.filter(ex => ex.requiredForSignoff);
    const completedRequiredItems = requiredItems.filter(ex => ex.status === 'complete' || ex.status === 'validated');
    
    if (requiredItems.length === 0) return 100;
    return Math.round((completedRequiredItems.length / requiredItems.length) * 100);
  };
  
  const readinessScore = calculateReadinessScore();
  
  // Determine if stage is locked (completed)
  const isLocked = stage.status === 'completed';
  
  // Get readiness class
  const getReadinessClass = (score: number) => {
    if (score >= 90) return "text-green-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };
  
  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Information Exchange</CardTitle>
              <CardDescription>Required deliverables for RIBA Stage {stage.id}: {stage.name}</CardDescription>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{completionPercentage}%</div>
              <div className="text-xs text-muted-foreground">Complete</div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-6 p-4 bg-gray-50 border rounded-lg">
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <div>
                <h3 className="font-medium mb-1">Stage Readiness</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  This shows how ready the stage is to progress based on required information exchanges.
                </p>
                
                <div className="flex items-center gap-2 mt-2">
                  <div className={`text-2xl font-bold ${getReadinessClass(readinessScore)}`}>
                    {readinessScore}%
                  </div>
                  <Button 
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={checkStageReadiness}
                  >
                    Check Readiness <ArrowUpRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="w-full sm:w-1/2">
                <div className="mb-1 flex justify-between text-xs">
                  <span>Not Ready</span>
                  <span>Ready</span>
                </div>
                <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${
                      readinessScore >= 90 ? 'bg-green-500' : 
                      readinessScore >= 50 ? 'bg-amber-500' : 
                      'bg-red-500'
                    }`}
                    style={{ width: `${readinessScore}%` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                  <span>0%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>
          </div>
          
          {infoExchanges.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <Info className="h-10 w-10 mb-2" />
              <h3 className="text-lg font-medium">No Information Exchange Requirements</h3>
              <p>This stage does not have any specified information exchange requirements.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {infoExchanges.map((exchange, index) => (
                <div key={exchange.id} className={`border rounded-md p-4 ${exchange.requiredForSignoff ? 'bg-amber-50 border-amber-200' : ''}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{exchange.name}</h3>
                        {exchange.requiredForSignoff && (
                          <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded">Required</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{exchange.description}</p>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">
                      {getStatusIcon(exchange.status)}
                      <span className="text-xs font-medium">{getStatusText(exchange.status)}</span>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    {exchange.documentUrl ? (
                      <div className="flex flex-col gap-2">
                        <div className="bg-muted rounded p-2 flex items-center justify-between">
                          <div className="flex items-center">
                            <FileCheck className="h-5 w-5 mr-2" />
                            <span className="text-sm truncate">{exchange.name} document</span>
                          </div>
                          <div className="flex items-center">
                            {exchange.dateUploaded && (
                              <span className="text-xs text-muted-foreground mr-3">
                                Uploaded: {new Date(exchange.dateUploaded).toLocaleDateString()}
                              </span>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => window.open(exchange.documentUrl, '_blank')}>
                              View
                            </Button>
                          </div>
                        </div>
                        
                        {!isLocked && (
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => openCommentDialog(exchange)}>
                              Add Comment
                            </Button>
                            <FileUpload
                              id={`upload-${exchange.id}`}
                              onUpload={(url) => handleUpload(exchange.id, url)}
                              disabled={isLocked || exchange.status === 'validated'}
                              buttonText="Replace File"
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <FileUpload
                          id={`upload-${exchange.id}`}
                          onUpload={(url) => handleUpload(exchange.id, url)}
                          disabled={isLocked}
                        />
                        {exchange.comments && (
                          <div className="mt-2 text-sm bg-muted p-2 rounded-md">
                            <p className="font-medium">Comments:</p>
                            <p>{exchange.comments}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {!isLocked && (
                <div className="flex justify-end mt-4">
                  <Button onClick={validateStage} disabled={isLocked}>
                    Validate Stage Information
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Comment Dialog */}
      <Dialog open={commentDialogOpen} onOpenChange={setCommentDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
            <DialogDescription>
              Add comments or notes about this information exchange item.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your comments here..."
              className="min-h-[100px]"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveComment}>Save Comment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Validation Dialog */}
      <Dialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Missing Required Information
            </DialogTitle>
            <DialogDescription>
              The following required information exchanges must be completed before validating this stage:
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ul className="list-disc pl-5 space-y-1">
              {missingRequiredItems.map((item, index) => (
                <li key={index} className="text-sm">{item}</li>
              ))}
            </ul>
          </div>
          <DialogFooter>
            <Button onClick={() => setValidationDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Readiness Check Dialog */}
      <Dialog open={readinessDialogOpen} onOpenChange={setReadinessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-500" />
              Stage Readiness Check
            </DialogTitle>
            <DialogDescription>
              Stage {stage.id}: {stage.name} readiness assessment
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="text-center mb-4">
              <div className={`text-4xl font-bold ${getReadinessClass(readinessScore)}`}>
                {readinessScore}%
              </div>
              <div className="text-sm text-muted-foreground mt-1">Readiness Score</div>
              <Progress value={readinessScore} className="mt-2" />
            </div>
            
            {missingRequiredItems.length > 0 ? (
              <>
                <h3 className="font-medium mb-2 flex items-center">
                  <AlertTriangle className="h-4 w-4 text-amber-500 mr-1" />
                  Missing required items:
                </h3>
                <ul className="list-disc pl-5 space-y-1 mb-4">
                  {missingRequiredItems.map((item, index) => (
                    <li key={index} className="text-sm">{item}</li>
                  ))}
                </ul>
              </>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center gap-2 mb-4">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span>All required information is complete!</span>
              </div>
            )}
            
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
              <p className="text-blue-800">
                <strong>Stage {stage.id} Assessment:</strong> {
                  readinessScore >= 90 
                    ? "This stage is ready to progress. All required information exchanges are complete." 
                    : readinessScore >= 50
                    ? "This stage is partially ready. Some required information is still missing."
                    : "This stage is not ready to progress. Several required items are missing."
                }
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setReadinessDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
